import { useRef, useState } from "react";
import { motion } from "framer-motion";

const PAIRS = [
  {
    title: "Muir Glacier, Alaska",
    left: "https://images.unsplash.com/photo-1517299321609-52687d1bc55a?w=1600&auto=format&fit=crop",
    right: "https://images.pexels.com/photos/30412602/pexels-photo-30412602.jpeg?auto=compress&cs=tinysrgb&w=1600",
    labelLeft: "1941",
    labelRight: "2024",
    note: "The Muir Glacier retreated more than 12 km in the second half of the 20th century.",
  },
  {
    title: "Aral Sea, Central Asia",
    left: "https://images.unsplash.com/photo-1500581276021-a4bbcd0050c5?w=1600&auto=format&fit=crop",
    right: "https://images.pexels.com/photos/36767372/pexels-photo-36767372.jpeg?auto=compress&cs=tinysrgb&w=1600",
    labelLeft: "1989",
    labelRight: "2024",
    note: "Once the world's fourth-largest lake, the Aral Sea has lost more than 90% of its surface area.",
  },
  {
    title: "California Sierra Nevada",
    left: "https://images.unsplash.com/photo-1600011689032-8b628b8a8747?w=1600&auto=format&fit=crop",
    right: "https://images.pexels.com/photos/7913319/pexels-photo-7913319.jpeg?auto=compress&cs=tinysrgb&w=1600",
    labelLeft: "1995",
    labelRight: "2023",
    note: "Wildfire seasons in California now start earlier and burn hotter — 5 of the largest fires on record occurred after 2020.",
  },
];

function Slider({ pair }) {
  const [pct, setPct] = useState(50);
  const ref = useRef(null);
  const drag = (e) => {
    const rect = ref.current.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const p = Math.max(0, Math.min(100, (x / rect.width) * 100));
    setPct(p);
  };
  return (
    <div className="mb-24 last:mb-0" data-testid={`before-after-${pair.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
      <div className="mb-6 flex items-baseline justify-between">
        <h3 className="font-serif-display text-4xl md:text-5xl tracking-tight">{pair.title}</h3>
        <span className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[color:var(--np-text-2)]">{pair.labelLeft} → {pair.labelRight}</span>
      </div>
      <div
        ref={ref}
        className="relative w-full aspect-[16/9] overflow-hidden rounded-sm select-none cursor-ew-resize bg-black"
        onMouseMove={(e) => e.buttons === 1 && drag(e)}
        onTouchMove={drag}
        onClick={drag}
      >
        <img src={pair.right} alt={pair.labelRight} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 overflow-hidden" style={{ clipPath: `inset(0 ${100 - pct}% 0 0)` }}>
          <img src={pair.left} alt={pair.labelLeft} className="w-full h-full object-cover" />
        </div>
        <div className="absolute top-4 left-4 font-mono-tech text-[10px] tracking-[0.3em] uppercase bg-black/50 backdrop-blur px-2 py-1">{pair.labelLeft}</div>
        <div className="absolute top-4 right-4 font-mono-tech text-[10px] tracking-[0.3em] uppercase bg-black/50 backdrop-blur px-2 py-1">{pair.labelRight}</div>
        <div
          className="absolute top-0 bottom-0 w-px bg-[color:var(--np-live)] pointer-events-none"
          style={{ left: `${pct}%` }}
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-10 h-10 rounded-full bg-[color:var(--np-live)] flex items-center justify-center text-black text-xs font-mono-tech"
          >⇔</motion.div>
        </div>
      </div>
      <p className="mt-4 text-sm text-[color:var(--np-text-2)] max-w-xl">{pair.note}</p>
    </div>
  );
}

export default function BeforeAfterSlider() {
  return (
    <section data-testid="before-after-section" className="py-24 md:py-40 px-6 md:px-10 max-w-[1600px] mx-auto bg-[color:var(--np-bg)]">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-20">
        <div className="md:col-span-4">
          <div className="font-mono-tech text-[10px] tracking-[0.4em] uppercase text-[color:var(--np-muted)] mb-4">/03 · Before & After</div>
          <h2 className="font-serif-display text-5xl md:text-7xl leading-[0.95] tracking-tighter">
            The record<br/>doesn't lie.
          </h2>
        </div>
        <p className="md:col-span-6 md:col-start-7 md:pt-8 text-[color:var(--np-text-2)] text-lg self-end">
          Drag the handle. Watch decades collapse into a single frame. These comparisons speak louder than any headline — glaciers, lakes, and forests, side by side with themselves.
        </p>
      </div>
      {PAIRS.map((p) => <Slider key={p.title} pair={p} />)}
    </section>
  );
}
